/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Linear Integration
 * 
 * Handles Linear issue and user story import/export.
 */

import { getProductServerBaseUrl } from './base';
import type { AcceptanceCriterion } from '@shared/types/user-stories';

export interface LinearConfig {
  apiKey: string;
  defaultTeamId?: string;
}

export interface LinearTeam {
  id: string;
  name: string;
  key: string;
}

export interface LinearProject {
  id: string;
  name: string;
  key: string;
}

export interface LinearTeamsResult {
  teams: LinearTeam[];
  projects: LinearProject[];
}

export interface LinearImportResult {
  total: number;
  successful: number;
  failed: number;
  updated: number;
  created: number;
  skipped: number;
  results: Array<{
    linearId: string;
    issueNumber: number;
    identifier: string;
    success: boolean;
    action?: 'created' | 'updated' | 'skipped';
    error?: string;
    skillshopIssue?: any;
  }>;
  errors: string[];
}

export interface LinearExportResult {
  successful: number;
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}

// ============================================================================
// Connection & Teams
// ============================================================================

/**
 * Test Linear connection
 * Product server endpoint - uses direct fetch
 */
export async function testLinearConnection(config: LinearConfig): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/import/test-connection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });

    const data = await response.json();
    
    if (data.success || data.data?.success) {
      return { success: true };
    }
    return { 
      success: false, 
      error: data.data?.error || data.error || 'Connection failed' 
    };
  } catch (err: any) {
    return { 
      success: false, 
      error: err.message || 'Failed to connect to Linear' 
    };
  }
}

/**
 * Get Linear teams and projects
 * Product server endpoint - uses direct fetch
 */
export async function getLinearTeams(config: LinearConfig): Promise<{ success: boolean; data?: LinearTeamsResult; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/import/teams`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });

    const data = await response.json();
    
    if (response.ok) {
      return { 
        success: true, 
        data: data.data || { teams: data.teams || [], projects: data.projects || [] }
      };
    }
    return { success: false, error: data.error || 'Failed to fetch teams' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to fetch teams' };
  }
}

// ============================================================================
// Issue Import (Issues, not User Stories)
// ============================================================================

/**
 * Preview Linear issue import
 * Product server endpoint - uses direct fetch
 */
export async function previewLinearIssueImport(config: LinearConfig, options?: {
  teamIds?: string[];
  includeCompleted?: boolean;
  maxResults?: number;
  query?: string;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/import/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        teamIds: options?.teamIds,
        includeCompleted: options?.includeCompleted || false,
        maxResults: options?.maxResults || 100,
        query: options?.query
      })
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error || 'Failed to load preview' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to load preview' };
  }
}

/**
 * Import Linear issues
 * Product server endpoint - uses direct fetch
 */
export async function importLinearIssues(config: LinearConfig, options?: {
  teamIds?: string[];
  includeCompleted?: boolean;
  maxResults?: number;
  query?: string;
  updateExisting?: boolean;
  repoUrl?: string;
  repoPath?: string;
}): Promise<{ success: boolean; data?: LinearImportResult; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/import/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        teamIds: options?.teamIds,
        includeCompleted: options?.includeCompleted || false,
        maxResults: options?.maxResults || 100,
        query: options?.query,
        updateExisting: options?.updateExisting || false,
        repoUrl: options?.repoUrl,
        repoPath: options?.repoPath,
      })
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error || 'Import failed' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Import failed' };
  }
}

// ============================================================================
// User Story Import/Export
// ============================================================================

/**
 * Preview Linear user story import
 * Product server endpoint - uses direct fetch
 */
export async function previewLinearUserStoryImport(config: {
  apiKey: string;
  defaultTeamId?: string;
}, options?: {
  teamIds?: string[];
  projectIds?: string[];
  includeCompleted?: boolean;
  maxResults?: number;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/user-stories/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        teamIds: options?.teamIds,
        projectIds: options?.projectIds,
        includeCompleted: options?.includeCompleted || false,
        maxResults: options?.maxResults || 100,
      })
    });

    const data = await response.json();

    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error || 'Failed to load preview' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to load preview. The import endpoint may not be implemented yet.' };
  }
}

/**
 * Import Linear user stories
 * Product server endpoint - uses direct fetch
 */
export async function importLinearUserStories(config: {
  apiKey: string;
  defaultTeamId?: string;
}, options?: {
  teamIds?: string[];
  projectIds?: string[];
  includeCompleted?: boolean;
  maxResults?: number;
  updateExisting?: boolean;
  repoUrl?: string;
  repoPath?: string;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/user-stories/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        teamIds: options?.teamIds,
        projectIds: options?.projectIds,
        includeCompleted: options?.includeCompleted || false,
        maxResults: options?.maxResults || 100,
        updateExisting: options?.updateExisting || false,
        repoUrl: options?.repoUrl,
        repoPath: options?.repoPath,
      })
    });

    const data = await response.json();

    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error || 'Import failed' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Import failed' };
  }
}

/**
 * Export user stories to Linear
 * Product server endpoint - uses direct fetch
 */
export async function exportUserStoriesToLinear(config: {
  apiKey: string;
  defaultTeamId?: string;
}, epics: Array<{
  id: string;
  title: string;
  description?: string;
  status?: string;
}>, stories: Array<{
  id: string;
  title: string;
  role?: string;
  goal?: string;
  benefit?: string;
  acceptanceCriteria?: AcceptanceCriterion[];
  epicId?: string;
  status?: string;
}>, options?: {
  teamId: string;
  createProjects?: boolean;
}): Promise<{ success: boolean; data?: LinearExportResult; error?: string }> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/linear/user-stories/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        epics,
        stories,
        options
      })
    });

    const data = await response.json();

    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error || 'Export failed' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Export failed. The export endpoint may not be implemented yet.' };
  }
}

